import { ReactNode, useEffect, useState } from "react";

import { Modal } from "./index";
import Button from "../button/Button";

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string; // default "Delete"
  cancelLabel?: string; // default "Cancel"
  // Red confirm button for destructive actions (default). Set false for a
  // neutral/primary confirm (e.g. a non-destructive acknowledgement).
  destructive?: boolean;
  // Optional external busy flag (e.g. the page's own saving state). The modal
  // ALSO guards against double-submit internally (see below), so this is only
  // needed when the page wants to drive the busy state itself.
  busy?: boolean;
  busyLabel?: string; // default "Working…"
  onConfirm: () => void;
  onClose: () => void;
}

// Styled replacement for window.confirm(), matching the app's inline
// delete-confirmation modals (see ExpensesPage etc.). Keeps confirmations
// visually consistent instead of falling back to the browser's native popup.
export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  destructive = true,
  busy = false,
  busyLabel = "Working…",
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  // Internal double-submit guard: once Confirm is clicked, disable both buttons
  // until the modal closes (the caller clears its state after the async action
  // resolves). Reset whenever the modal is (re)opened. This makes every
  // ConfirmModal safe against a fast double-click without each caller having to
  // thread a `busy` flag.
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!isOpen) setSubmitting(false);
  }, [isOpen]);
  // ...and re-arm when the caller's own busy flag drops while the modal is
  // still open. That is an action that FAILED (success closes the modal): the
  // caller shows why — or asks a follow-up question — and the operator must be
  // able to retry or cancel. Without this the dialog stayed on "Deleting…"
  // with both buttons dead. Callers that pass no busy flag never change it,
  // so for them nothing changes.
  useEffect(() => {
    if (!busy) setSubmitting(false);
  }, [busy]);

  const disabled = busy || submitting;

  const handleConfirm = () => {
    setSubmitting(true);
    onConfirm();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-sm p-5 mx-4">
      <h4 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white/90">{title}</h4>
      <div className="mb-5 text-sm text-gray-600 dark:text-gray-400">{message}</div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={disabled}>
          {cancelLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={handleConfirm}
          disabled={disabled}
          // Drop the red override when disabled so it greys out like a normal
          // disabled primary button instead of staying a dimmed red.
          className={destructive && !disabled ? "!bg-error-500 hover:!bg-error-600" : ""}
        >
          {disabled ? busyLabel : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
